import React, { useMemo } from 'react'
import { Tag, Space } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { PaperClipOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { documentsApi } from '@/api/services'
import type { DocumentSummary } from '@/types'
import {
  getColumnSearchProps,
  getColumnEnumFilterProps,
  textMatchesColumnFilter,
} from '@/components/table/columnSearchProps'
import { DOC_TYPE_FILTER_OPTIONS } from '@/components/table/approvalTableConstants'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { EllipsisCell } from '@/components/table/EllipsisCell'
import { Button } from '@/components/ui/Button'
import { useNavigate, Navigate } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/stores/authStore'
import { isPodTransporter } from '@/utils/authPaths'
import { transporterDocumentJourneyPath } from '@/utils/transporterNav'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { Route } from 'lucide-react'

function attachmentCountBadgeClass(n: number): string {
  if (n <= 0) return 'border-border bg-muted text-muted-foreground'
  if (n === 1) return 'border-sky-200 bg-sky-100 text-sky-900 dark:border-sky-800 dark:bg-sky-950/80 dark:text-sky-100'
  if (n === 2) return 'border-emerald-200 bg-emerald-100 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-50'
  if (n === 3) return 'border-amber-200 bg-amber-100 text-amber-950 dark:border-amber-800 dark:bg-amber-950/70 dark:text-amber-50'
  return 'border-rose-200 bg-rose-100 text-rose-950 dark:border-rose-800 dark:bg-rose-950/70 dark:text-rose-50'
}

type DocumentJourneyPageProps = {
  /** When true, list is scoped to the logged-in transporter (server-enforced). */
  transporterScoped?: boolean
}

const DocumentJourneyPage: React.FC<DocumentJourneyPageProps> = ({ transporterScoped }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isTransporterUser = isPodTransporter(user)
  const isTransporterView = transporterScoped ?? isTransporterUser
  const transporterCode = String(user?.transporterCode || '').trim()

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['documents-journey', isTransporterView ? 'transporter' : 'all', transporterCode],
    queryFn: () => documentsApi.listDocuments({ limit: 500 }),
    select: (res: any) => res.data?.data,
    enabled: !isTransporterView || !!transporterCode,
  })

  const docs = data ?? []
  const normalizedDocs: DocumentSummary[] = useMemo(() => {
    // Backward/forward compatible: API returns either `types` or `typesCsv`
    return docs.map((d: any) => {
      const types: string[] = Array.isArray(d.types)
        ? d.types
        : typeof d.typesCsv === 'string' && d.typesCsv.trim()
          ? d.typesCsv.split(',').map((s: string) => s.trim()).filter(Boolean)
          : d.latestType
            ? [d.latestType]
            : []
      return {
        ...d,
        types,
        latestType: d.latestType || types[0] || 'ALT',
        customerCode: d.customerCode ?? null,
        materialCodeNo: d.materialCodeNo ?? null,
        year: d.year ?? null,
        app: d.app ?? null,
        postingDate: d.postingDate ?? null,
        quantity: d.quantity ?? null,
        actual_qty: d.actual_qty ?? null,
        excess_qty: d.excess_qty ?? null,
        short_qty: d.short_qty ?? null,
        vehicleNo: d.vehicleNo ?? null,
        lrNo: d.lrNo ?? null,
        transporterCode: d.transporterCode ?? null,
        transporterName: d.transporterName ?? null,
      } as DocumentSummary
    })
  }, [docs])

  const columns = useMemo(
    () => [
      {
        title: t('DocumentJourney:columns.att'),
        key: 'attachmentsBadge',
        dataIndex: 'attachmentsCount',
        width: 76,
        align: 'center' as const,
        className: 'align-middle',
        sorter: (a: DocumentSummary, b: DocumentSummary) =>
          Number(a.attachmentsCount) - Number(b.attachmentsCount),
        render: (n: number) => (
          <div className="flex justify-center py-1">
            <div
              className={cn(
                'flex min-h-[2.5rem] min-w-[2.5rem] flex-col items-center justify-center rounded-lg border px-2 py-1.5 shadow-sm transition-colors',
                attachmentCountBadgeClass(Number(n)),
              )}
              title={t('DocumentJourney:table.attachmentsTooltip', { count: n })}
            >
              <PaperClipOutlined className="text-[15px]" />
              <span className="text-xs font-semibold tabular-nums leading-tight">{n}</span>
            </div>
          </div>
        ),
      },
      {
        title: t('DocumentJourney:columns.documentNo'),
        dataIndex: 'documentNo',
        key: 'documentNo',
        ellipsis: true,
        sorter: (a: DocumentSummary, b: DocumentSummary) =>
          String(a.documentNo).localeCompare(String(b.documentNo), undefined, { numeric: true }),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.documentNo'), (value, record) =>
          textMatchesColumnFilter(value, record.documentNo)
        ),
      },
      {
        title: t('DocumentJourney:columns.columnType'),
        dataIndex: 'types',
        key: 'type',
        render: (_: unknown, r: DocumentSummary) => (
          <Space size={[4, 4]} wrap>
            {r.types.map((type) => (
              <Tag
                key={type}
                color={type === 'ALT' ? 'blue' : type === 'DHR' ? 'green' : type === 'GRN' ? 'orange' : 'purple'}
              >
                {type}
              </Tag>
            ))}
          </Space>
        ),
        // Filter by the latest type for now (still grouped only by document number).
        ...getColumnEnumFilterProps<DocumentSummary>(t('DocumentJourney:columns.columnType'), DOC_TYPE_FILTER_OPTIONS, (r) => r.latestType),
      },
      {
        title: t('DocumentJourney:columns.columnCustomer'),
        dataIndex: 'customerName',
        key: 'customerName',
        ellipsis: true,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.columnCustomer'), (value, record) =>
          textMatchesColumnFilter(value, record.customerName)
        ),
      },
      {
        title: t('DocumentJourney:columns.custCode'),
        dataIndex: 'customerCode',
        key: 'customerCode',
        width: 100,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.customerCode == null || r.customerCode === '' ? '—' : r.customerCode),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.custCode'), (value, record) =>
          textMatchesColumnFilter(value, record.customerCode ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.material'),
        dataIndex: 'materialCodeNo',
        key: 'materialCodeNo',
        width: 110,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.materialCodeNo == null || r.materialCodeNo === '' ? '—' : r.materialCodeNo),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.material'), (value, record) =>
          textMatchesColumnFilter(value, record.materialCodeNo ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.year'),
        dataIndex: 'year',
        key: 'year',
        width: 72,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.year == null || r.year === '' ? '—' : r.year),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.year'), (value, record) => textMatchesColumnFilter(value, record.year ?? '')),
      },
      {
        title: t('DocumentJourney:columns.app'),
        dataIndex: 'app',
        key: 'app',
        width: 80,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.app == null || r.app === '' ? '—' : r.app),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.app'), (value, record) => textMatchesColumnFilter(value, record.app ?? '')),
      },
      {
        title: t('DocumentJourney:columns.posting'),
        dataIndex: 'postingDate',
        key: 'postingDate',
        width: 110,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) =>
          r.postingDate == null || r.postingDate === '' ? '—' : r.postingDate,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.posting'), (value, record) =>
          textMatchesColumnFilter(value, record.postingDate ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.columnFrom'),
        dataIndex: 'fromLocation',
        key: 'fromLocation',
        ellipsis: true,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.columnFrom'), (value, record) =>
          textMatchesColumnFilter(value, record.fromLocation)
        ),
      },
      {
        title: t('DocumentJourney:columns.columnTo'),
        dataIndex: 'toLocation',
        key: 'toLocation',
        ellipsis: true,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.columnTo'), (value, record) =>
          textMatchesColumnFilter(value, record.toLocation)
        ),
      },
      {
        title: t('DocumentJourney:columns.qty'),
        dataIndex: 'quantity',
        key: 'quantity',
        width: 80,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.quantity == null || r.quantity === '' ? '—' : r.quantity),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.qty'), (value, record) => textMatchesColumnFilter(value, record.quantity ?? '')),
      },
      {
        title: t('DocumentJourney:columns.actual'),
        dataIndex: 'actual_qty',
        key: 'actual_qty',
        width: 80,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) =>
          r.actual_qty == null || r.actual_qty === '' ? '—' : r.actual_qty,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.actual'), (value, record) =>
          textMatchesColumnFilter(value, record.actual_qty ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.vehicle'),
        dataIndex: 'vehicleNo',
        key: 'vehicleNo',
        width: 104,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.vehicleNo == null || r.vehicleNo === '' ? '—' : r.vehicleNo),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.vehicle'), (value, record) =>
          textMatchesColumnFilter(value, record.vehicleNo ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.lr'),
        dataIndex: 'lrNo',
        key: 'lrNo',
        width: 88,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (r.lrNo == null || r.lrNo === '' ? '—' : r.lrNo),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.lr'), (value, record) => textMatchesColumnFilter(value, record.lrNo ?? '')),
      },
      {
        title: t('DocumentJourney:columns.tpt'),
        dataIndex: 'transporterCode',
        key: 'transporterCode',
        width: 90,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) =>
          r.transporterCode == null || r.transporterCode === '' ? '—' : r.transporterCode,
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.tpt'), (value, record) =>
          textMatchesColumnFilter(value, record.transporterCode ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.transporter'),
        dataIndex: 'transporterName',
        key: 'transporterName',
        width: 130,
        ellipsis: true,
        render: (_: unknown, r: DocumentSummary) => (
          <EllipsisCell>{r.transporterName == null || r.transporterName === '' ? '—' : r.transporterName}</EllipsisCell>
        ),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.transporter'), (value, record) =>
          textMatchesColumnFilter(value, record.transporterName ?? '')
        ),
      },
      {
        title: t('DocumentJourney:columns.lastUpdate'),
        dataIndex: 'lastCreatedAt',
        key: 'lastCreatedAt',
        width: 140,
        render: (d: string | null) => (d ? dayjs(d).format('DD-MM-YYYY') : '-'),
        ...getColumnSearchProps<DocumentSummary>(t('DocumentJourney:columns.lastUpdate'), (value, record) =>
          textMatchesColumnFilter(value, record.lastCreatedAt ? dayjs(record.lastCreatedAt).format('DD-MM-YYYY') : '')
        ),
      },
    ],
    [t]
  )

  if (isTransporterUser && !transporterScoped) {
    return <Navigate to={transporterDocumentJourneyPath()} replace />
  }

  return (
    <PageShell>
      <PageHero
        title={t('DocumentJourney:header.title')}
        subtitle={
          isTransporterView
            ? t('DocumentJourney:header.transporterDesc', { code: transporterCode || '—' })
            : t('DocumentJourney:header.standardDesc')
        }
        badges={[{ label: 'Journey', accent: true, icon: Route }]}
        actions={
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {t('DocumentJourney:actions.refresh')}
          </Button>
        }
      />

      <DashboardPanel
        title={t('DocumentJourney:table.cardTitle')}
        subtitle={isTransporterView ? t('DocumentJourney:table.cardTransporterDesc') : t('DocumentJourney:table.cardStandardDesc')}
        flush
        className="leap-data-page-card"
      >
        <LeapDataTable<DocumentSummary>
          dataSource={normalizedDocs}
          columns={columns as any}
          rowKey={r => String(r.documentNo)}
          loading={isLoading}
          pagination={leapTablePagination(normalizedDocs.length)}
          onRow={record => ({
            onClick: () =>
              navigate(
                isTransporterView
                  ? transporterDocumentJourneyPath(record.documentNo)
                  : `/documentJourney/${record.documentNo}`
              ),
            style: { cursor: 'pointer' },
          })}
        />
      </DashboardPanel>
    </PageShell>
  )
}

export default DocumentJourneyPage

